import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useFetch } from "@/core/api/common/composables/useFetch";
import { useCurrency } from "@/core/composables";
import { useDataAreaId } from "@/core/composables/useDataAreaId";
import { Carrier } from "@/core/enums/carriers.enum";
import { globals } from "@/core/globals";
import { buildShippingMethod, getTransitTimeDisplay } from "@/core/utilities";
import { useThorlabsGtm } from "@/shared/gtm/gtm";
import { eventBus } from "@/shared/thorlabs/shared";
import { useCheckoutFacade } from "../composables/useCheckoutFacade";
import type { ShippingChoiceType } from "../types";
import type { InputShipmentTypeExtension, ShippingMethodType } from "@/core/api/graphql/types";

// ERP carrierName casing varies by data area (e.g. "Fedex" vs "FedEx"), so compare case-insensitively.
function isCarrier(carrierName: string | undefined, carrier: Carrier): boolean {
  return carrierName?.toUpperCase() === carrier.toUpperCase();
}

export function convertSpecialCountryForShippingChoices(
  countryCode: string | undefined,
  regionId?: string | undefined,
) {
  if (countryCode === "ICA") {
    return "ESP";
  }
  if (countryCode === "USA" && regionId === "PR") {
    return "PRI";
  }
  return countryCode;
}

export function useShippingMethod() {
  const { currentCurrency } = useCurrency();
  const { gtmSendAddShippingInfoEventAsync } = useThorlabsGtm();
  const { t } = useI18n();
  const { dataAreaId } = useDataAreaId();
  const {
    checkoutShipment,
    cartAvailableShippingMethods,
    deliveryAddress: cartDeliveryAddress,
    cartWeight,
    cartId,
    cartHasUkHeavyItems,
    cartHasVaporCells,
    isCustomerOrder,
    updateUsdSalesTax,
    setShippingMethod,
  } = useCheckoutFacade();

  const selectedShippingMethod = ref("");
  const shippingChoices = ref<ShippingChoiceType[] | []>([]);
  const weightExceeded = ref<number>(0);

  const isOnlySpecialShippingMethod = computed<boolean>(() => {
    return (
      // Cart settings union with currencySetting
      (cartAvailableShippingMethods.value.length === 1 && cartAvailableShippingMethods.value[0].code === "Special") ||
      cartHasUkHeavyItems.value ||
      cartHasVaporCells.value ||
      // WeightExceeded value that come from fetchShippingChoices
      weightExceeded.value > 0 ||
      // If we are on acceptQuote flow and shipping method already setted as Special
      (isCustomerOrder && checkoutShipment.value?.shipmentMethodOption === "Special")
    );
  });

  const dhlChoices = computed<ShippingChoiceType[] | null>(() => {
    const choices = shippingChoices.value?.filter((method) => isCarrier(method.carrierName, Carrier.DHL)) || null;
    return choices?.sort((a, b) => a.totalFreightAmount - b.totalFreightAmount) || null;
  });

  const fedExChoices = computed<ShippingChoiceType[] | null>(() => {
    const choices = shippingChoices.value?.filter((method) => isCarrier(method.carrierName, Carrier.FedEx)) || null;
    return (
      choices?.sort((a, b) => {
        if (a.totalFreightAmount !== b.totalFreightAmount) {
          return a.totalFreightAmount - b.totalFreightAmount;
        }
        return a.modeText.localeCompare(b.modeText);
      }) || null
    );
  });

  const upsChoices = computed<ShippingChoiceType[] | null>(() => {
    const choices = shippingChoices.value?.filter((method) => isCarrier(method.carrierName, Carrier.UPS)) || null;
    return choices?.sort((a, b) => a.totalFreightAmount - b.totalFreightAmount) || null;
  });

  const isFedExExpediteIt = computed<boolean>(() => {
    const econFedEx = fedExChoices.value?.find((choice) => choice.rateModeId === "FedEx-Econ");
    const groundFedEx = fedExChoices.value?.find((choice) => choice.rateModeId === "FedEx-Grou");

    return !!(econFedEx && groundFedEx && econFedEx.totalFreightAmount === groundFedEx.totalFreightAmount);
  });

  const hasNoShippingChoices = computed<boolean>(() => {
    return shippingChoices.value && shippingChoices.value.length === 0;
  });

  const specialShipmentComment = computed<string>(() => {
    const comments: string[] = [];

    if (cartHasUkHeavyItems.value) {
      comments.push("Cart Weight Exceeded UK Heavy Items");
    }

    if (cartHasVaporCells.value) {
      comments.push("Cart Contains Vapor Cells");
    }

    if (weightExceeded.value > 0) {
      comments.push("Cart Weight Exceeded Carrier Limit");
    }

    if (comments.length === 0) {
      return "";
    }

    return comments.join("| ");
  });

  async function fetchShippingChoices() {
    try {
      const cartWeightInKg = (cartWeight.value ?? 0) / 2.205;
      const { error, data } = await useFetch(
        "/api/shipping/getShippingChoices",
        // Uncomment this part for suppressing the error notifications for this fetch.
        // Passing options replaces every factory hook not re-supplied here, so the interceptors that add
        // the Authorization header have to be replayed via `const { onRequest, onResponse } = useGlobalInterceptors()`.
        // {
        // async beforeFetch(ctx) {
        //   await Promise.all(onRequest.value.map((intercept) => intercept(ctx.url, ctx.options)));
        //   return ctx;
        // },
        // async afterFetch(ctx) {
        //   await Promise.all(onResponse.value.map((intercept) => intercept(ctx.response)));
        //   return ctx;
        // },
        // onFetchError(ctx) {
        //   return ctx;
        // },
        // }
      )
        .post({
          toZip: cartDeliveryAddress.value?.postalCode,
          countryCode: convertSpecialCountryForShippingChoices(
            cartDeliveryAddress.value?.countryCode,
            cartDeliveryAddress.value?.regionId,
          ),
          dataAreaId: dataAreaId.value,
          orderWeight: cartWeightInKg,
          currencyCode: globals.currencyCode,
        })
        .json<ShippingChoiceType[]>();

      if (!data.value || error.value) {
        console.error("Error fetching shipping choices:", error.value);
        eventBus.emit("showShippingChoiceFormError", { message: t("shared.checkout.shipping_details_section.error") });
        return;
      } else {
        shippingChoices.value = data.value;
        selectedShippingMethod.value =
          shippingChoices.value.find((choice) => choice.modeText === checkoutShipment.value?.shipmentMethodOption)
            ?.rateModeId || "";
        if (shippingChoices.value.length === 1 && shippingChoices.value[0].modeText === "WeightLimitExceeded") {
          if (currentCurrency.value.code === "USD") {
            weightExceeded.value = shippingChoices.value[0].maxWeightLimitKg * 2.205;
          } else {
            weightExceeded.value = shippingChoices.value[0].maxWeightLimitKg;
          }

          weightExceeded.value = Math.round(weightExceeded.value);
          console.log("Weight Limit Exceeded:", weightExceeded.value);
        } else {
          weightExceeded.value = 0;
        }
      }
      eventBus.emit("hideShippingChoiceFormError");
    } catch (err) {
      eventBus.emit("showShippingChoiceFormError", { message: t("shared.checkout.shipping_details_section.error") });
    }
  }

  function setShippingChoices(choices: ShippingChoiceType[]) {
    shippingChoices.value = choices;
  }

  function setSelectedShippingMethod(rateModeId: string) {
    selectedShippingMethod.value = rateModeId;
  }

  function setWeightExceeded(weight: number) {
    weightExceeded.value = weight;
  }

  async function applyShippingMethod(
    shouldCalculateUsdSalesTax: boolean,
    wantsFewerPackages: boolean,
    shippingAccountNumberRecalculateTotals: string,
    shouldSendGtmEvent: boolean = false,
  ) {
    try {
      const shipingChoice = shippingChoices.value.find((choice) => choice.rateModeId === selectedShippingMethod.value);

      const shippingMethod = buildShippingMethod(
        shipingChoice,
        isOnlySpecialShippingMethod.value || hasNoShippingChoices.value,
        currentCurrency.value,
      ) as ShippingMethodType;

      if (shouldCalculateUsdSalesTax) {
        const inputShipmentMethod = {
          id: checkoutShipment.value?.id,
          price: shippingMethod.price?.amount,
          weight: cartWeight.value,
          shipmentMethodCode: shippingMethod.code,
          shipmentMethodOption: shippingMethod.optionName,
          wantsFewerPackagesTL: wantsFewerPackages,
          shippingAccountNumberTL: shippingAccountNumberRecalculateTotals,
          businessDaysToShipTL: getTransitTimeDisplay(shipingChoice as ShippingChoiceType, t),
          vendorId: shipingChoice?.rateModeId,
        } as InputShipmentTypeExtension;

        await updateUsdSalesTax({
          storeId: globals.storeId,
          currencyCode: globals.currencyCode,
          cultureName: globals.cultureName,
          cartId: cartId.value,
          userId: globals.userId,
          shipment: inputShipmentMethod,
        });
      } else {
        await setShippingMethod(
          shippingMethod,
          wantsFewerPackages,
          shippingAccountNumberRecalculateTotals,
          specialShipmentComment.value,
          getTransitTimeDisplay(shipingChoice as ShippingChoiceType, t),
          shipingChoice?.rateModeId,
        );
      }

      if (shouldSendGtmEvent) {
        await gtmSendAddShippingInfoEventAsync(`${shippingMethod.optionName}`);
      }
      eventBus.emit("hideShippingChoiceFormError");
    } catch (err) {
      console.error("Error applying shipping method:", err);
      eventBus.emit("showShippingChoiceFormError", {
        message: t("shared.checkout.shipping_details_section.error"),
      });
    }
  }

  return {
    shippingChoices,
    dhlChoices,
    fedExChoices,
    upsChoices,
    selectedShippingMethod,
    weightExceeded,
    hasNoShippingChoices,
    isOnlySpecialShippingMethod,
    isFedExExpediteIt,

    // actions
    setShippingChoices,
    setSelectedShippingMethod,
    setWeightExceeded,
    fetchShippingChoices,
    applyShippingMethod,
  };
}
